import i18next from "i18next";

// ── Tipler ──────────────────────────────────────────────────────

export type ConfiguratorModuleKind = string;

export type ModuleCatalogSource =
  | { type: "attribute"; key: string }
  | { type: "material"; part: "fabric" | "color" | "full" };

export type ModuleCatalogEntry = {
  id: string;
  label: string;
  kind: string;
  attributes?: Record<string, string | ModuleCatalogSource[]>;
};

/**
 * `api/configurator/zip/{id}` yanıtının `moduleCatalog` alanı.
 * Eski `klem-module-catalog.json` şemasının aynısı (`_readme` hariç).
 */
export type ModuleCatalog = {
  displayFields: Record<string, Record<string, ModuleCatalogSource[]>>;
  resolve: Record<string, ModuleCatalogEntry | ModuleCatalogEntry[]>;
  optionNameAliases?: Record<string, string>;
};

/** Sahne modellerinden sadece kullandığımız alanların yapısal tipi. */
interface SceneConfiguratorPart {
  name?: string;
}

interface SceneConfigurator {
  mainConfig?: Map<string, SceneConfiguratorPart>;
  sideConfig?: Map<string, SceneConfiguratorPart>;
}

interface SceneModel {
  userData?: { info?: { id?: number; name?: string }; [key: string]: unknown };
  data?: { id?: number };
  configurator?: SceneConfigurator;
  partMaterialGroups?: ReadonlyArray<{ material?: { name?: string } }>;
}

export type ResolvedModuleWithFeatures = {
  id: string;
  key: string;
  label: string;
  kind: string;
  groupIndex: number;
  module: string;
  fabric: string;
  color: string;
  summary: string;
  features: Record<string, string>;
};

// ── Katalog index'i ─────────────────────────────────────────────

/** Bir katalogdan türetilen, çözümleme sırasında kullanılan arama yapıları. */
type CatalogIndex = {
  fields: Record<string, Record<string, ModuleCatalogSource[]>>;
  resolve: Record<string, ModuleCatalogEntry[]>;
  pairKeys: string[];
  legIds: Set<number>;
  canon: (s: string) => string;
};

const _indexCache = new WeakMap<ModuleCatalog, CatalogIndex>();

function buildCatalogIndex(catalog: ModuleCatalog): CatalogIndex {
  const fields = catalog.displayFields ?? {};
  const aliases = catalog.optionNameAliases ?? {};
  const kinds = new Set(Object.keys(fields));

  const resolve: Record<string, ModuleCatalogEntry[]> = {};
  for (const [key, row] of Object.entries(catalog.resolve ?? {})) {
    const entries = (Array.isArray(row) ? row : [row]).filter(
      (e) => e?.id && e.label && e.kind && kinds.has(e.kind),
    );
    if (entries.length) resolve[key] = entries;
  }

  const keys = Object.keys(resolve);
  const pairKeys = keys.filter((k) => /^\d+\.\d+$/.test(k));

  const legIds = new Set<number>();
  for (const k of keys) {
    const m = k.match(/^(\d+)#/);
    if (m) legIds.add(+m[1]);
  }

  return { fields, resolve, pairKeys, legIds, canon: (s) => aliases[s] ?? s };
}

/** Katalog nesnesi başına index'i bir kez hesaplar, sonrasında cache'ten döner. */
function getCatalogIndex(catalog: ModuleCatalog): CatalogIndex {
  let index = _indexCache.get(catalog);
  if (!index) {
    index = buildCatalogIndex(catalog);
    _indexCache.set(catalog, index);
  }
  return index;
}

// ── Materyal yardımcıları ───────────────────────────────────────

function splitMaterial(name: string) {
  const s = name.trim();
  if (!s) return { fabric: "", color: "" };
  const i = s.indexOf("-");
  if (i < 0) return { fabric: "", color: s };
  return { fabric: s.slice(0, i).trim(), color: s.slice(i + 1).trim() };
}

function getModelMaterial(model: SceneModel): string {
  for (const g of model.partMaterialGroups ?? []) {
    if (g?.material?.name) return String(g.material.name).trim();
  }
  return "";
}

function getModelId(m: SceneModel): number | undefined {
  const id = m.userData?.info?.id ?? m.data?.id;
  return typeof id === "number" ? id : undefined;
}

// ── Sahne → variant attrs ───────────────────────────────────────

function extractSceneAttrs(group: SceneModel[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const node of group) {
    const cfg = node.configurator;
    if (!cfg) continue;

    if (cfg.mainConfig?.get) {
      const leg = cfg.mainConfig.get("legIn");
      const back = cfg.mainConfig.get("backOut");
      if (leg?.name && !out.legType) out.legType = String(leg.name).trim();
      if (back?.name && !out.backCushion) out.backCushion = String(back.name).trim();
    }

    if (!out.armType && cfg.sideConfig?.size) {
      const byKey = cfg.sideConfig.get("side");
      const part = byKey?.name?.trim()
        ? byKey
        : Array.from(cfg.sideConfig.values()).find((p) => p?.name?.trim());
      if (part?.name) out.armType = String(part.name).trim();
    }
  }
  return out;
}

// ── Display options oluşturma ───────────────────────────────────

function buildDisplayOptions(
  entry: ModuleCatalogEntry,
  materialName: string,
  sceneCtx: Record<string, string>,
  index: CatalogIndex,
) {
  const ctx = { ...sceneCtx };
  if (entry.attributes) {
    for (const [k, v] of Object.entries(entry.attributes)) {
      if (typeof v === "string" && !ctx[k]) ctx[k] = v;
    }
  }

  const { fabric, color } = splitMaterial(materialName);
  const materialPart = (p: string) =>
    p === "fabric" ? fabric : p === "color" ? (color || materialName.trim()) : materialName.trim();

  const seen = new Set<string>();
  const out: { name: string; value: string }[] = [];

  const tryField = (field: string, sources: ModuleCatalogSource[]) => {
    const c = index.canon(field);
    if (seen.has(c)) return;
    seen.add(c);
    for (const src of sources) {
      const v = src.type === "attribute" ? (ctx[src.key] ?? "").trim() : materialPart(src.part);
      if (v) {
        out.push({ name: c, value: v });
        return;
      }
    }
  };

  for (const [field, sources] of Object.entries(index.fields[entry.kind] ?? {})) {
    tryField(field, sources);
  }
  if (entry.attributes) {
    for (const [field, val] of Object.entries(entry.attributes)) {
      if (Array.isArray(val)) tryField(field, val);
    }
  }

  return out;
}

// ── Sahne pool eşleme ───────────────────────────────────────────

type PoolEntry = { id: number; model: SceneModel };

function takeFromPool(pool: PoolEntry[], id: number): PoolEntry | undefined {
  const i = pool.findIndex((e) => e.id === id);
  return i < 0 ? undefined : pool.splice(i, 1)[0];
}

function countInPool(pool: PoolEntry[], id: number): number {
  return pool.filter((e) => e.id === id).length;
}

function buildResolvedRow(key: string, entry: ModuleCatalogEntry, groupIndex: number, materialName: string) {
  const { fabric, color } = splitMaterial(materialName);
  const module = String(i18next.t(entry.label, { defaultValue: entry.label }));
  const summary = [module, fabric, color].filter(Boolean).join(" ");
  return { id: entry.id, key, label: entry.label, kind: entry.kind, groupIndex, module, fabric, color, summary: summary || module, entry };
}

function buildFallbackEntry(id: number, model: SceneModel): ModuleCatalogEntry {
  const name = model.userData?.info?.name?.trim();
  return { id: String(id), label: name || String(id), kind: "" };
}

function matchModelsFromPool(pool: PoolEntry[], groupIndex: number, index: CatalogIndex) {
  const out: ReturnType<typeof buildResolvedRow>[] = [];

  for (const key of index.pairKeys) {
    const [, a, b] = key.match(/^(\d+)\.(\d+)$/)!;
    const entries = index.resolve[key];
    if (!entries) continue;
    while (countInPool(pool, +a) > 0 && countInPool(pool, +b) > 0) {
      const ea = takeFromPool(pool, +a);
      const eb = takeFromPool(pool, +b);
      if (!ea || !eb) break;
      const material = getModelMaterial(ea.model) || getModelMaterial(eb.model);
      for (const entry of entries) {
        out.push(buildResolvedRow(key, entry, groupIndex, material));
      }
    }
  }

  for (const meshId of index.legIds) {
    const count = countInPool(pool, meshId);
    if (count <= 0) continue;
    const entries = index.resolve[`${meshId}#${count}`];
    if (!entries) continue;
    let firstModel: SceneModel | undefined;
    for (let i = 0; i < count; i++) {
      const taken = takeFromPool(pool, meshId);
      if (taken && !firstModel) firstModel = taken.model;
    }
    const material = firstModel ? getModelMaterial(firstModel) : "";
    for (const entry of entries) {
      out.push(buildResolvedRow(`${meshId}#${count}`, entry, groupIndex, material));
    }
  }

  while (pool.length) {
    const { id, model } = pool.shift()!;
    const material = getModelMaterial(model);
    const entries = index.resolve[String(id)] ?? [buildFallbackEntry(id, model)];
    for (const entry of entries) {
      out.push(buildResolvedRow(String(id), entry, groupIndex, material));
    }
  }

  return out;
}

// ── Public API ──────────────────────────────────────────────────

export function resolveSceneModulesWithFeatures(
  products: unknown,
  catalog?: ModuleCatalog,
): ResolvedModuleWithFeatures[] {
  if (!Array.isArray(products) || !catalog) return [];

  const index = getCatalogIndex(catalog);
  const out: ResolvedModuleWithFeatures[] = [];

  products.forEach((group: unknown, groupIndex: number) => {
    if (!Array.isArray(group) || !group.length) return;
    const first = group[0];
    if (!first || typeof first !== "object" || !("userData" in first || "data" in first)) return;

    const models = group as SceneModel[];
    const pool: PoolEntry[] = [];
    for (const m of models) {
      const id = getModelId(m);
      if (id !== undefined) pool.push({ id, model: m });
    }

    for (const resolved of matchModelsFromPool(pool, groupIndex, index)) {
      const materialLine = resolved.fabric && resolved.color
        ? `${resolved.fabric} - ${resolved.color}`
        : resolved.fabric || resolved.color || "";
      const ctx = extractSceneAttrs(models);
      const { entry, ...moduleRow } = resolved;
      const options = buildDisplayOptions(entry, materialLine, ctx, index);
      const features = Object.fromEntries(options.map((o) => [o.name, o.value]));
      out.push({ ...moduleRow, features });
    }
  });

  return out;
}
